import { useMemo } from 'react';

import ProductCartCounter from '../ProductCartCounter';
import { isValidProductUrl } from '../../utils/segmentation';
import { stripHtml } from '../../utils/sanitizeHtml';
import { sanitizeText } from '../../utils/sanitizeContent';
import { useSegmentation } from './SegmentationRenderer';

function formatProductPrice(value) {
  if (value == null || value === '') return '₹0.00';
  const normalized = Number(String(value).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(normalized)) return '₹0.00';
  return `₹${normalized.toFixed(2)}`;
}

function productIdentity(product, index) {
  const id = product?.product_id ?? product?.id ?? product?.sku;
  if (id != null && String(id).trim()) return String(id).trim();
  const name = String(product?.product_name || product?.name || '').trim().toLowerCase();
  return name || `product-${index}`;
}

export function normalizeProductList(products) {
  if (!products) return [];

  if (Array.isArray(products)) {
    const seen = new Set();
    return products.filter((product, idx) => {
      const key = productIdentity(product, idx);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  let morning = [];
  let evening = [];

  if (Array.isArray(products.morning) || Array.isArray(products.evening)) {
    morning = products.morning || [];
    evening = products.evening || [];
  } else if (products.recommended_products) {
    const nested = products.recommended_products;
    if (Array.isArray(nested)) {
      return normalizeProductList(nested);
    }
    morning = nested.morning || [];
    evening = nested.evening || [];
  }

  const byId = new Map();

  const upsert = (product, routine, index) => {
    if (!product || typeof product !== 'object') return;
    const key = productIdentity(product, index);
    const existing = byId.get(key);
    if (existing) {
      if (routine && !existing.routines.includes(routine)) {
        existing.routines.push(routine);
      }
      return;
    }
    byId.set(key, {
      product,
      routines: routine ? [routine] : [],
    });
  };

  morning.forEach((product, idx) => upsert(product, 'Morning', idx));
  evening.forEach((product, idx) => upsert(product, 'Evening', idx));

  return Array.from(byId.values()).map(({ product, routines }) => ({
    ...product,
    _routines: routines,
  }));
}

function getRecommendationLine(product) {
  if (product.suggested_for_wording) {
    return String(product.suggested_for_wording).replace(/\(score:\s*(\d+)\)/gi, '').trim();
  }
  const conditionsToShow = (product.suggested_for_conditions?.length > 0)
    ? product.suggested_for_conditions
    : (product.concerns_treated || []);
  if (!conditionsToShow.length) return '';
  return `Recommended for ${conditionsToShow.join(', ')}`;
}

const ProductCards = ({
  products,
  cartItems = {},
  onAddToCart,
  onViewProduct,
}) => {
  const segmentation = useSegmentation();
  const list = useMemo(() => {
    const normalized = normalizeProductList(products);
    const limit = segmentation.productSuggestLimit;
    if (typeof limit === 'number' && limit >= 1) {
      return normalized.slice(0, limit);
    }
    return normalized;
  }, [products, segmentation.productSuggestLimit]);

  if (!segmentation.loaded || !segmentation.productSuggestion || list.length === 0) {
    return null;
  }

  return (
    <div className="skin-routine-section-block skin-routine-products-block">
      <div className="skin-routine-section-heading-with-info">
        <h2 className="skin-routine-section-heading">Recommended Products</h2>
      </div>
      <div className="skin-routine-product-recommendations-content product-recommendations-content">
        <div className="product-grid">
          {list.map((product, idx) => {
            const productId = product.product_id || product.id || product.sku
              || `prod_${(product.product_name || product.name || '').replace(/\s+/g, '_')}_${idx}`;
            const cartQuantity = cartItems[productId]?.quantity || 0;
            const recommendationLine = getRecommendationLine(product);
            const badgeText = recommendationLine
              ? `Recommended for ${recommendationLine.replace(/^Recommended for\s*/i, '')}`
              : null;
            const productTypeDisplay = product.product_type || product.productType
              || (Array.isArray(product.product_types) && product.product_types?.[0])
              || product.product_types;
            const productLink = product.product_link || product.link || '';
            const showProductLinkCta = segmentation.productLink && isValidProductUrl(productLink);
            const majorUspPlain = product.major_usp ? stripHtml(product.major_usp) : '';
            const name = sanitizeText(product.product_name || product.name || 'Product');

            return (
              <div key={productId} className="product-card">
                {badgeText ? <div className="product-recommendation-badge">{badgeText}</div> : null}
                <div className="product-card-image">
                  {product.image_url && (
                    <img
                      src={product.image_url}
                      alt={name}
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                <div className="product-info">
                  <div className="product-name-header">
                    {productLink ? (
                      <a
                        className="product-name product-name-link"
                        href={productLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {name}
                      </a>
                    ) : (
                      <span className="product-name">{name}</span>
                    )}
                  </div>
                  {productTypeDisplay && (
                    <div className="product-type-heading">Type: {productTypeDisplay}</div>
                  )}
                  {majorUspPlain && (
                    <div className="product-major-usp-wrapper">
                      <span className="product-card-usp-heading">Key benefit</span>
                      <span className="product-major-usp-text">{majorUspPlain}</span>
                    </div>
                  )}
                  {product.concerns_treated?.length > 0 && (
                    <div className="product-info-section">
                      <div className="product-info-label">
                        <img
                          src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                          alt="Treats"
                          className="product-info-icon"
                        />
                        <span>Treats</span>
                      </div>
                      <div className="product-tags-container">
                        {product.concerns_treated.map((concern, i) => (
                          <span key={`${productId}-concern-${i}`} className="product-tag">{concern}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="product-card-footer">
                  <div className="product-price-quantity">
                    <span className="product-price">
                      {formatProductPrice(product.sale_price ?? product.price ?? product.amount ?? product.mrp)}
                    </span>
                    {(product.sale_price || product.discount) && (product.amount || product.price || product.mrp) ? (
                      <span className="product-original-price">
                        {formatProductPrice(product.amount ?? product.price ?? product.mrp)}
                      </span>
                    ) : null}
                  </div>
                  {segmentation.addToCart && onAddToCart ? (
                    <ProductCartCounter
                      key={`counter-${productId}`}
                      product={product}
                      onAddToCart={(nextProduct, quantity) => onAddToCart(nextProduct, quantity, productId)}
                      initialQuantity={cartQuantity}
                    />
                  ) : null}
                  {showProductLinkCta && onViewProduct ? (
                    <button
                      type="button"
                      className="skin-action-button product-link-button"
                      onClick={(e) => { e.stopPropagation(); onViewProduct(product, productLink); }}
                      title="Open product page"
                    >
                      View product
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductCards;
